import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  CircularProgress, 
  Alert, 
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider
} from '@mui/material';
import { Search, Clear, ArrowUpward, ArrowDownward, Refresh } from '@mui/icons-material';
import apiService from '../../services/apiService';
import '../styles/ContentComponents.css';

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('create_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    newThisMonth: 0
  });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, appliedSearchTerm, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      include_sensitive: false,
      search: appliedSearchTerm || null,
      sort_by: sortBy,
      sort_order: sortOrder
    };
    
    const result = await apiService.fetchUsersPaginated(params);
    
    if (result.success) {
      const { users: usersData, pagination: paginationData } = result.data;
      
      setUsers(usersData);
      setPagination(prev => ({
        ...prev,
        page: paginationData.page,
        limit: paginationData.limit,
        totalCount: paginationData.total_count,
        totalPages: paginationData.total_pages,
        hasNext: paginationData.has_next,
        hasPrev: paginationData.has_prev
      }));
      
      // Calculate statistics from current page data
      // Note: For accurate stats, we'd need a separate endpoint for statistics
      const active = usersData.filter(user => user.activated).length;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const newThisMonth = usersData.filter(user => {
        if (!user.create_date) return false;
        const createDate = new Date(user.create_date);
        return createDate.getMonth() === currentMonth && createDate.getFullYear() === currentYear;
      }).length;
      
      setStats({ 
        total: paginationData.total_count,
        active, 
        newThisMonth 
      });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleRetry = () => {
    fetchUsers();
  };

  const handleRowClick = (user) => {
    navigate(`/user/${user.unique_id}`);
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const applySearch = () => {
    setAppliedSearchTerm(searchTerm);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      applySearch();
    }
  };

  // Sort handlers
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when sorting
  };

  const getFullName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.user_name;
  };

  const getRolesDisplay = (roles) => {
    if (!roles || roles.length === 0) return 'No roles';
    return roles.join(', ');
  };

  // Define table columns
  const columns = [
    {
      key: 'unique_id',
      label: 'User ID',
      type: 'truncate',
      maxLength: 20
    },
    {
      key: 'user_name',
      label: 'Username'
    },
    {
      key: 'first_name',
      label: 'Full Name',
      render: (value, item) => getFullName(item)
    },
    {
      key: 'roles',
      label: 'Roles',
      render: (value) => getRolesDisplay(value)
    },
    {
      key: 'activated',
      label: 'Status',
      render: (value) => (
        <Chip 
          label={value ? 'Active' : 'Inactive'} 
          color={value ? 'success' : 'error'} 
          size="small" 
          variant="outlined"
        />
      )
    },
    {
      key: 'organization_ids',
      label: 'Projects',
      render: (value) => `${value ? value.length : 0} projects`
    },
    {
      key: 'opt_in_updates',
      label: 'Opt-in Updates',
      render: (value) => (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          color={value ? 'success' : 'default'} 
          size="small" 
          variant="outlined"
        />
      )
    },
    {
      key: 'create_date',
      label: 'Created Date',
      type: 'date',
      render: (value) => value ? apiService.formatDate(value) : 'N/A'
    }
  ];

  // Render cell content based on column type
  const renderCell = (item, column) => {
    const value = item[column.key];
    
    if (column.render) {
      return column.render(value, item);
    }
    
    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : 'N/A';
    }
    
    if (column.type === 'truncate' && value && value.length > column.maxLength) {
      return (
        <span title={value}>
          {value.slice(0, column.maxLength)}...
        </span>
      );
    }
    
    return value || 'N/A';
  };

  // Loading skeleton for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.limit }).map((_, index) => (
        <TableRow key={index}>
          {columns.map((column) => (
            <TableCell key={column.key} sx={{ py: 0.5 }}>
              <Skeleton variant="text" width="80%" height={16} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <Box sx={{ 
      height: '100vh',
      maxHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'grey.50'
    }}>
      {/* Header Section */}
      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            👥 Users Management
          </Typography>
        </Box>
        
        {/* Search and Controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton size="small" onClick={clearSearch}>
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={applySearch}
              disabled={loading}
              size="small"
              sx={{ minWidth: 80 }}
            >
              Search
            </Button>
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => {
                setSortBy(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <MenuItem value="create_date">Created Date</MenuItem>
              <MenuItem value="user_name">Username</MenuItem>
              <MenuItem value="first_name">First Name</MenuItem>
              <MenuItem value="last_name">Last Name</MenuItem>
              <MenuItem value="activated">Status</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton
            onClick={() => handleSortChange(sortBy)}
            color="primary"
            size="small"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pagination.limit}
              label="Per Page"
              onChange={handleLimitChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {/* Results info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {users.length} of {pagination.totalCount} users
            {appliedSearchTerm && ` for "${appliedSearchTerm}"`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sorted by: {sortBy.replace('_', ' ')} ({sortOrder})
          </Typography>
        </Box>
      </Paper>

      {/* Error State */}
      {error && (
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Table Section */}
      <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
         <TableContainer 
           component={Paper} 
           sx={{ 
             height: 'calc(100vh - 290px)', // Reduced height to show fewer rows
             borderRadius: 0,
             overflow: 'auto',
             '& .MuiTable-root': {
               tableLayout: 'fixed'
             }
           }}
         >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    sx={{ 
                      fontWeight: 600,
                      bgcolor: 'grey.100',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      py: 1
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {appliedSearchTerm 
                          ? `No results found for "${appliedSearchTerm}"`
                          : 'Try adjusting your search criteria'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow 
                    key={user.unique_id || index} 
                    hover
                    onClick={() => handleRowClick(user)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'grey.25'
                      }
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell 
                        key={column.key}
                        sx={{ 
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          py: 0.5
                        }}
                      >
                        {renderCell(user, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Paper elevation={1} sx={{ p: 1, borderRadius: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default Users; 